import { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { Button } from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table/data-table";
import { createActionsColumn } from "../../../components/ui/data-table/columns";
import { SearchInput } from "../../../components/ui/search-input";
import { Edit2, Trash2 } from "lucide-react";
import deactivateIcon from "assets/images/deactivateicon.svg";
import activateIcon from "assets/images/CheckSquare.svg";
import { toast } from "react-toastify";
import serviceableZonesService from "../../../services/ServiceableZonesService";
import LocationDropdown from "components/LocationDropdown/LocationDropdown";
import { FormDialog } from "../../../components/ui/form-dialog";
import AddServiceableZoneForm, { validateZoneForm } from "../../../components/ServiceableZoneForm/AddServiceableZoneForm";
import { NewPagination } from "../../../components/ui/newPagination";
import CommonDialog from "components/commonDialog/CommonDialog";

const getDefaultFormData = () => ({
  name: "",
  locationId: "",
  lat: "",
  lng: "",
  radiusKm: "",
});

export default function ServiceableZones() {
  const dispatch = useDispatch();
  const selectedLocation = useSelector(
    (state) => state?.locationDropdown?.selectedLocation
  );

  const [zonesData, setZonesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingZoneId, setEditingZoneId] = useState(null);
  const [formData, setFormData] = useState(getDefaultFormData());

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteZoneId, setDeleteZoneId] = useState(null);
  const [deleteZoneName, setDeleteZoneName] = useState("");

  const fetchZones = async (locationId, search, page = currentPage, limit = itemsPerPage) => {
    try {
      let query = {};
      if (search || searchQuery) {
        query.search = encodeURIComponent((search ?? searchQuery).trim());
      }
      if (locationId) {
        query.locationId = locationId;
      }
      query.page = page;
      query.limit = limit;

      const res = await serviceableZonesService.getServiceableZones(query);
      const headers = res?.data?.header || [];
      const body = res?.data?.body || [];
      setTotalItems(res?.data?.totalCount || 0);

      const dynamicColumns = headers.map(({ key, label }) => {
        if (key === "status") {
          return {
            accessorKey: key,
            header: label,
            cell: ({ row }) => {
              const status = row.original.status;
              const isActive = status === "Active" || status === true;
              return (
                <span className={`textTransformInherit textCaptialize fontNormal ${status}Btn`}>
                  {status || "—"}
                </span>
              );
            },
          };
        }
        return { accessorKey: key, header: label };
      });

      const actionsColumn = {
        accessorKey: "actions",
        header: "Action",
        cell: ({ row }) =>
          createActionsColumn([
            {
              label: "Edit",
              icon: <Edit2 size={15} />,
              onClick: () => {
                const cp = row.original.centerPoint;
                // GeoJSON Point: coordinates = [lng, lat]
                const lat = cp?.coordinates?.[1] ?? cp?.lat ?? "";
                const lng = cp?.coordinates?.[0] ?? cp?.lng ?? "";
                setFormData({
                  name: row.original.name,
                  locationId: row.original.locationId,
                  lat,
                  lng,
                  radiusKm: row.original.radiusKm,
                });
                setEditingZoneId(row.original.id);
                setIsEditing(true);
                setIsDialogOpen(true);
              },
            },
            {
              label: row.original.status === "Active" ? "Deactivate" : "Activate",
              icon: row.original.status === "Active"
                ? <img src={deactivateIcon} alt="Deactivate" style={{ width: 15, height: 15 }} />
                : <img src={activateIcon} alt="Activate" style={{ width: 15, height: 15 }} />,
              onClick: async () => {
                try {
                  const newStatus = row.original.status === "Active" ? "InActive" : "Active";
                  const cp = row.original.centerPoint;
                  const payload = {
                    name: row.original.name,
                    locationId: row.original.locationId,
                    centerPoint: {
                      lat: cp?.coordinates?.[1] ?? cp?.lat,
                      lng: cp?.coordinates?.[0] ?? cp?.lng,
                    },
                    radiusKm: row.original.radiusKm,
                    status: newStatus,
                  };
                  const res = await serviceableZonesService.updateServiceableZone(row.original.id, payload);
                  toast.success(res?.message || `Zone marked as ${newStatus} successfully`);
                  fetchZones(selectedLocation?.id, undefined, currentPage, itemsPerPage);
                } catch (error) {
                  toast.error(error?.response?.data?.message || "Failed to update status");
                }
              },
            },
            {
              label: "Delete",
              icon: <Trash2 size={15} className="text-red-500" />,
              onClick: () => {
                setDeleteZoneId(row.original.id);
                setDeleteZoneName(row.original.name);
                setIsDeleteDialogOpen(true);
              },
            },
          ]).cell({ row }),
      };

      setZonesData(body);
      setColumns([...dynamicColumns, actionsColumn]);
      setFilteredData(body);
    } catch (error) {
      toast.error("Failed to fetch serviceable zones");
    }
  };

  useEffect(() => {
    fetchZones(selectedLocation?.id);
    // eslint-disable-next-line
  }, []);

  const handleLocationChange = (location) => {
    setCurrentPage(1);
    fetchZones(location?.id);
  };

  const handleAddOrUpdate = async () => {
    if (!formData.name || !formData.locationId || !formData.lat || !formData.lng || !formData.radiusKm) return;

    try {
      const payload = {
        name: formData.name,
        locationId: formData.locationId,
        centerPoint: {
          lat: parseFloat(formData.lat),
          lng: parseFloat(formData.lng),
        },
        radiusKm: parseFloat(formData.radiusKm)
      };

      if (isEditing && editingZoneId) {
        const res = await serviceableZonesService.updateServiceableZone(editingZoneId, payload);
        toast.success(res?.message || "Serviceable zone updated successfully");
      } else {
        const res = await serviceableZonesService.createServiceableZone(payload);
        toast.success(res?.message || "Serviceable zone added successfully");
      }

      setFormData(getDefaultFormData());
      setIsEditing(false);
      setEditingZoneId(null);
      setIsDialogOpen(false);
      setCurrentPage(1);
      fetchZones(selectedLocation?.id, undefined, 1, itemsPerPage);
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : null) ||
        error?.message ||
        (isEditing ? "Failed to update serviceable zone" : "Failed to add serviceable zone");
      toast.error(apiMessage);
    }
  };

  const handleCancel = () => {
    setFormData(getDefaultFormData());
    setIsEditing(false);
    setEditingZoneId(null);
    setIsDialogOpen(false);
  };

  const debouncedFetch = useCallback(
    debounce((locationId, query) => {
      fetchZones(locationId, query);
    }, 500),
    []
  );

  const handleSearch = (query) => {
    setSearchQuery(query || "");
    debouncedFetch(selectedLocation?.id, query || "");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchZones(selectedLocation?.id, searchQuery, page, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchZones(selectedLocation?.id, searchQuery, 1, newItemsPerPage);
  };

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const handleOpenAdd = () => {
    setIsEditing(false);
    setEditingZoneId(null);
    setFormData(getDefaultFormData());
    setIsDialogOpen(true);
  };

  const isSubmitDisabled = !validateZoneForm(formData);

  return (
    <div className="container mx-auto pt-10 pb-0">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-[15px]">
        <div className="flex items-center gap-4">
          <Box sx={{ width: "200px" }}>
            <LocationDropdown
              onChange={handleLocationChange}
              value={selectedLocation}
              selected={selectedLocation}
              placeholder="All City"
              fullWidth
            />
          </Box>
        </div>

        <div className="flex items-center gap-2">
          <SearchInput onSearch={handleSearch} className="w-[250px]" />
          <FormDialog
            trigger={
              <Button
                className="h-[40px] bg-[#ffa726] hover:bg-[#ffa726] rounded-[5px] font-normal text-[14px] text-white border border-white capitalize"
                onClick={handleOpenAdd}
              >
                + Add Zone
              </Button>
            }
            title={isEditing ? "Edit" : "Add"}
            subtitle="Serviceable Zone"
            isOpen={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            onSubmit={handleAddOrUpdate}
            onCancel={handleCancel}
            submitLabel={isEditing ? "Update" : "Create"}
            submitDisabled={isSubmitDisabled}
          >
            <AddServiceableZoneForm
              formData={formData}
              onFormDataChange={setFormData}
            />
          </FormDialog>
        </div>
      </div>

      <div>
        <DataTable
          columns={columns}
          data={filteredData}
        />
        <NewPagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          className="border-t"
        />
      </div>

      <CommonDialog
        open={isDeleteDialogOpen}
        handleClose={() => setIsDeleteDialogOpen(false)}
        toggle={setIsDeleteDialogOpen}
        onConfirmDialog={async () => {
          try {
            const res = await serviceableZonesService.deleteServiceableZone(deleteZoneId);
            toast.success(res?.message || `Zone "${deleteZoneName}" deleted successfully`);
            setIsDeleteDialogOpen(false);
            fetchZones(selectedLocation?.id);
          } catch (error) {
            const apiMessage =
              error?.response?.data?.message ||
              (typeof error?.response?.data === "string" ? error.response.data : null) ||
              error?.message ||
              "Failed to delete serviceable zone";
            toast.error(apiMessage);
            setIsDeleteDialogOpen(false);
          }
        }}
        text={`Are you sure you want to delete "${deleteZoneName}"?`}
        selectType="delete"
        dialogContent="serviceableZoneDelete"
        typeOfDialog="Serviceable Zone"
      />


    </div>
  );
}
